import classNames from 'classnames';
import PropTypes from 'prop-types';
import { stringify as stringifyQuery } from 'query-string';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useIndexUrlGenerator } from '../../contexts/RoutesContext';
import Body from './Body';
import Link from './Link';
import Partners from './Partners';

import styles from '../../../styles/partials/film-description.module.scss';

const propTypes = {
    item: AppPropTypes.film,
    className: PropTypes.string,
    isDark: PropTypes.bool,
    withPartners: PropTypes.bool,
};

const defaultProps = {
    item: null,
    className: null,
    isDark: false,
    withPartners: false,
};

const FilmDescription = ({ item, className, isDark, withPartners }) => {
    const {
        description = null,
        sections = [],
        types = [],
        premiereType = null,
        partners = [],
        awards = null,
        pressQuotes = null,
        additionalInformation = null,
        questionsAnswers = null,
        edition = null,
    } = item || {};

    const { handle: editionHandle } = edition || {};
    const route = useIndexUrlGenerator('films');
    const tags = useMemo(
        () =>
            [
                premiereType !== null
                    ? {
                          id: premiereType.id,
                          label: premiereType.label,
                          url: `${route('index_edition', {
                              edition: editionHandle,
                          })}?${stringifyQuery({
                              premiere_type: premiereType.id,
                          })}`,
                      }
                    : null,
                ...sections.map(({ id, label, slug }) => ({
                    id,
                    label,
                    url: route('index_with_taxonomy_and_edition', {
                        taxonomy: slug,
                        edition: editionHandle,
                    }),
                })),
                ...types.map(({ id, label }) => ({
                    id,
                    label,
                    url: `${route('index_edition', {
                        edition: editionHandle,
                    })}?${stringifyQuery(
                        {
                            type: [id],
                        },
                        { arrayFormat: 'bracket' },
                    )}`,
                })),
            ].filter((it) => it !== null),
        [item],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.isDark]: isDark,
                    [className]: className !== null,
                },
            ])}
        >
            {tags !== null && tags.length > 0 ? (
                <div className={styles.tagsContainer}>
                    <div className={styles.tags}>
                        {tags.map(({ url, label }, idx) => (
                            <Link
                                key={`${url}-${label}-${idx + 1}`}
                                href={url}
                                className={styles.tag}
                            >
                                {label}
                            </Link>
                        ))}
                    </div>
                </div>
            ) : null}

            {description !== null ? <Body className={styles.synopsis} body={description} /> : null}

            {questionsAnswers !== null ? (
                <div className={styles.questionsAnswers}>
                    {/* <h2 className={styles.heading}>
                        <FormattedMessage defaultMessage="Q&amp;A" description="Metadata label" />
                    </h2> */}
                    <Body className={styles.questionsAnswer} body={questionsAnswers} />
                </div>
            ) : null}

            {awards !== null ? (
                <div className={styles.awards}>
                    <h2 className={styles.heading}>
                        <FormattedMessage
                            defaultMessage="Festivals & Awards"
                            description="Metadata label"
                        />
                    </h2>
                    <Body className={styles.award} body={awards} />
                </div>
            ) : null}

            {pressQuotes !== null ? (
                <div className={styles.quotes}>
                    <h2 className={styles.heading}>
                        <FormattedMessage
                            defaultMessage="Press quotes"
                            description="Metadata label"
                        />
                    </h2>
                    {(pressQuotes || []).map((quote, idx) => (
                        <Body key={`${quote}-${idx + 1}`} className={styles.quote} body={quote} />
                    ))}
                </div>
            ) : null}

            {additionalInformation !== null ? (
                <div className={styles.additionalInformation}>
                    <h2 className={styles.heading}>
                        <FormattedMessage defaultMessage="More info" description="Metadata label" />
                    </h2>
                    <Body className={styles.information} body={additionalInformation} />
                </div>
            ) : null}

            {partners.length > 0 ? (
                <div className={styles.partners}>
                    <Partners isDark={isDark} withPartners={withPartners} partners={partners} />
                </div>
            ) : null}
        </div>
    );
};
FilmDescription.propTypes = propTypes;
FilmDescription.defaultProps = defaultProps;

export default FilmDescription;
